import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonToolbar,
  Form,
  InputNumber,
  InputPicker,
  Message,
  Schema,
  SelectPicker,
  Stack,
  toaster,
  Toggle,
} from 'rsuite';
import { AxiosError } from 'axios';
import { ItemDataType } from 'rsuite/CascadeTree';
import { isEmpty } from 'lodash';
import {
  Eshop,
  EshopSetup,
  getEshopSetup,
  saveEshopSetup,
  getPartnerEshopAutomaticLabelsPrintSetup,
  savePartnerEshopAutomaticLabelsPrintSetup,
} from '../../api/eshop/eshop';
import { utils } from '../../utils/utils';
import RecalculateAlzaBranchIds from '../alza/RecalculateAlzaBranchIds';
import EshopSetupDetailBaseMarketplace from './EshopSetupDetailBaseMarketplace';
import { OrderStates, PartnerEshopAutomaticLabelsPrintSetup }
  from '../../api/apiTypes';
import useIsPartnerDeactivated from '../../hooks/isPartnerDeactivatedHook';

export enum Input {
  BASE_MARKETPLACE,
  SYNCHRONIZATION,
  CLIENT_KEY,
  CLIENT_SECRET,
  STOCK_THRESHOLD,
  EXPEDITION_DAYS,
  FIXED_FEE,
  SUBSTITUTED_PHONE_NUMBER,
  DYNAMIC_BRANCH_ID,
  PACKETA_ID,
  PACKETA_API_KEY,
  INVOICE_REQUIRED,
  CREDIT_NOTE_REQUIRED,
}

export interface Labels {
  clientKey: string;
  clientSecret: string;
  stockThreshold: string;
  expeditionDays: string;
  fixedFee: string;
  dynamicBranchId: string;
  substitutedPhoneNumber: string;
  packetaId: string;
  packetaApiKey: string;
  invoiceRequired: string;
  creditNoteRequired: string;
}

interface Props {
  credentials?: JSX.Element;
  eshop: Eshop;
  inputs?: Input[];
  labels: Labels;
  baseMarketplaceCodes?: string[];
  onSuccessfulSave: () => void;
  onIsBaseMarketplaceUpdate?: (value: boolean) => void;
}

interface EshopEdit {
  isBaseMarketplace: boolean;
  clientKey: string;
  clientSecret: string;
  stockThreshold: string;
  expeditionDays: string;
  fixedFee: string;
  isEnabled: boolean;
  dynamicBranchId?: boolean;
  substitutedPhoneNumber: string;
  baseMarketplaceCode: string;
  packetaId: string,
  packetaApiKey: string,
  invoiceRequired?: boolean;
  creditNoteRequired?: boolean;
  automaticLabelsPrintEnabled: boolean;
  automaticLabelsPrintOrderStatus?: OrderStates;
}

const defaultEshopEdit: EshopEdit = {
  isBaseMarketplace: true,
  clientKey: '',
  clientSecret: '',
  stockThreshold: '',
  expeditionDays: '',
  fixedFee: '',
  isEnabled: false,
  substitutedPhoneNumber: '',
  baseMarketplaceCode: '',
  packetaId: '',
  packetaApiKey: '',
  automaticLabelsPrintEnabled: false,
  automaticLabelsPrintOrderStatus: undefined,
};

const minimumThreshold = 0;
const minimumExpeditionDays = 0;

const { StringType, NumberType } = Schema.Types;

function buildValidationModel(labels: Labels, inputs?: Input[]) {
  return Schema.Model({
    baseMarketplaceCode: inputs?.includes(Input.BASE_MARKETPLACE)
      ? Schema.Types.StringType().addRule((value, eshopEdit) => {
        if (eshopEdit.isBaseMarketplace) {
          return true;
        }

        return !!value;
      }, 'Base marketplace is required.', true)
      : StringType(),
    clientKey: inputs?.includes(Input.CLIENT_KEY)
      ? StringType().isRequired(`${labels.clientKey} is required.`)
      : StringType(),
    clientSecret: inputs?.includes(Input.CLIENT_SECRET)
      ? StringType().isRequired(`${labels.clientSecret} is required.`)
      : StringType(),
    stockThreshold: inputs?.includes(Input.STOCK_THRESHOLD)
      ? NumberType()
        .min(minimumThreshold, `${labels.stockThreshold} must be equal or greater than ${minimumThreshold}`)
      : NumberType(),
    expeditionDays: inputs?.includes(Input.EXPEDITION_DAYS)
      ? NumberType()
        .min(minimumExpeditionDays, `${labels.expeditionDays} must be equal or greater than ${minimumExpeditionDays}`)
        .isRequired(`${labels.expeditionDays} are required.`)
      : NumberType(),
    fixedFee: NumberType(),
    automaticLabelsPrintOrderStatus: Schema.Types.StringType().addRule((value, eshopEdit) => {
      if (!eshopEdit.automaticLabelsPrintEnabled) {
        return true;
      }

      return !isEmpty(value);
    }, 'Select order state for automatic labels print.', true),
  });
}

function mapEshopSetup(
  eshopSetup: EshopSetup | undefined,
  automaticLabelsPrintSetup: PartnerEshopAutomaticLabelsPrintSetup | undefined,
): EshopEdit {
  if (!eshopSetup) {
    return defaultEshopEdit;
  }
  return {
    isBaseMarketplace: !eshopSetup.baseMarketplaceCode,
    clientKey: eshopSetup.id,
    clientSecret: eshopSetup.clientId,
    stockThreshold: eshopSetup.stockThreshold?.toString(10) ?? '',
    expeditionDays: eshopSetup.expeditionDays?.toString(10) ?? '',
    fixedFee: eshopSetup.fixedFee?.toString(10) ?? '',
    substitutedPhoneNumber: eshopSetup?.substitutedPhoneNumber ?? '',
    isEnabled: eshopSetup.enableSyncWithEshop,
    dynamicBranchId: eshopSetup.dynamicBranchId,
    baseMarketplaceCode: eshopSetup.baseMarketplaceCode ?? '',
    packetaId: eshopSetup.packetaId ?? '',
    packetaApiKey: eshopSetup.packetaApiKey ?? '',
    invoiceRequired: eshopSetup.invoiceRequired,
    creditNoteRequired: eshopSetup.creditNoteRequired,
    automaticLabelsPrintEnabled: automaticLabelsPrintSetup?.automaticLabelsPrintEnabled ?? false,
    automaticLabelsPrintOrderStatus: automaticLabelsPrintSetup?.automaticLabelsPrintOrderStatus,
  };
}

function mapEshopEditRecord(record: Record<string, any>): EshopSetup {
  const eshopEdit = record as EshopEdit;
  return {
    enableSyncWithEshop: eshopEdit.isBaseMarketplace && eshopEdit.isEnabled,
    id: eshopEdit.clientKey ?? '',
    clientId: eshopEdit.clientSecret ?? '',
    stockThreshold: +eshopEdit.stockThreshold,
    expeditionDays: +eshopEdit.expeditionDays,
    fixedFee: +eshopEdit.fixedFee,
    dynamicBranchId: eshopEdit.dynamicBranchId,
    substitutedPhoneNumber: utils.defaultToUndefined(eshopEdit.substitutedPhoneNumber),
    baseMarketplaceCode: eshopEdit.baseMarketplaceCode || undefined,
    packetaId: eshopEdit.packetaId || undefined,
    packetaApiKey: eshopEdit.packetaApiKey || undefined,
    invoiceRequired: eshopEdit.invoiceRequired,
    creditNoteRequired: eshopEdit.creditNoteRequired,
  };
}

function mapAutomaticLabelsPrintEditRecord(record: Record<string, any>)
  : PartnerEshopAutomaticLabelsPrintSetup {
  const eshopEdit = record as EshopEdit;
  return {
    automaticLabelsPrintEnabled: eshopEdit.automaticLabelsPrintEnabled,
    automaticLabelsPrintOrderStatus: eshopEdit.automaticLabelsPrintOrderStatus,
  };
}

function mapBaseMarketplaceCodes(baseMarketplaceCodes?: string[]): ItemDataType[] {
  return baseMarketplaceCodes
    ?.map(baseMarketplaceCode => ({
      label: baseMarketplaceCode,
      value: baseMarketplaceCode,
    })) ?? [];
}

export default function EshopSetupDetail({
  credentials,
  eshop,
  inputs,
  labels,
  baseMarketplaceCodes,
  onSuccessfulSave,
  onIsBaseMarketplaceUpdate,
}: Props) {
  const [eshopSetup, setEshopSetup] = useState<EshopSetup>();
  const [
    automaticLabelsPrintSetup,
    setAutomaticLabelsPrintSetup,
  ] = useState<PartnerEshopAutomaticLabelsPrintSetup>();
  const [
    eshopEdit,
    setEshopEdit,
  ] = useState<Record<string, any>>(defaultEshopEdit);
  const formRef = useRef<any>();
  const validationModel = buildValidationModel(labels, inputs);
  const isPartnerDeactivated = useIsPartnerDeactivated();

  function showSuccessMessage() {
    toaster.push(
      <Message
        type="success"
        closable
        showIcon
        duration={2000}
      >
        Eshop settings successfully saved.
      </Message>,
    );
  }

  async function fetchEshopSetup() {
    const eshopSetupPromise = getEshopSetup(eshop.code)
      .catch((error: AxiosError) => {
        if (error.response?.status !== 404) {
          throw error;
        }
        return undefined;
      });

    const automaticLabelsSetupPromise = getPartnerEshopAutomaticLabelsPrintSetup(eshop.code)
      .catch((error: AxiosError) => {
        if (error.response?.status !== 404) {
          throw error;
        }
        return undefined;
      });

    const [eshopSetupResponse, automaticLabelsSetupResponse] = await Promise.all(
      [eshopSetupPromise, automaticLabelsSetupPromise],
    );

    setEshopSetup(eshopSetupResponse);
    setAutomaticLabelsPrintSetup(automaticLabelsSetupResponse);
    setEshopEdit(mapEshopSetup(eshopSetupResponse, automaticLabelsSetupResponse));
  }

  useEffect(() => {
    formRef.current?.cleanErrors();
    fetchEshopSetup().then();
  }, [eshop]);

  useEffect(() => {
    onIsBaseMarketplaceUpdate?.(eshopEdit.isBaseMarketplace);
  }, [eshopEdit.isBaseMarketplace]);

  function onSave(): void {
    if (!formRef.current?.check()) {
      return;
    }

    const updatedEshopSetup = { ...eshopSetup, ...mapEshopEditRecord(eshopEdit) };
    const updatedAutomaticLabelsPrintSetup = {
      ...automaticLabelsPrintSetup,
      ...mapAutomaticLabelsPrintEditRecord(eshopEdit),
    };

    Promise.all([
      saveEshopSetup(eshop.code, updatedEshopSetup),
      savePartnerEshopAutomaticLabelsPrintSetup(eshop.code, updatedAutomaticLabelsPrintSetup),
    ]).then(() => {
      showSuccessMessage();
      onSuccessfulSave();
    });
  }

  function onCancel(): void {
    setEshopEdit(mapEshopSetup(eshopSetup, automaticLabelsPrintSetup));
  }

  const orderStates = Object.values(OrderStates)
    .sort((a, b) => a.localeCompare(b))
    .map(s => ({ value: s, label: s }));

  return (
    <Stack>
      <Form
        layout="horizontal"
        formValue={eshopEdit}
        onChange={setEshopEdit}
        model={validationModel}
        ref={formRef}
        fluid
      >
        {
          inputs?.includes(Input.BASE_MARKETPLACE) && (
          <EshopSetupDetailBaseMarketplace
            isBaseMarketplace={eshopEdit.isBaseMarketplace}
            onUpdate={(value) => setEshopEdit({
              ...eshopEdit,
              isBaseMarketplace: value,
              baseMarketplaceCode: value ? '' : eshopEdit.baseMarketplaceCode,
            })}
          />
          )
        }
        {
          inputs?.includes(Input.BASE_MARKETPLACE) && !eshopEdit.isBaseMarketplace && (
          <Form.Group>
            <Form.ControlLabel>
              Base marketplace
            </Form.ControlLabel>
            <Form.Control
              name="baseMarketplaceCode"
              accepter={SelectPicker}
              value={eshopEdit.baseMarketplaceCode}
              data={mapBaseMarketplaceCodes(baseMarketplaceCodes)}
            />
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.SYNCHRONIZATION) && (
          <Form.Group>
            <Form.ControlLabel>
              Enable sync with
              {' '}
              {eshop.code}
            </Form.ControlLabel>
            <Toggle
              checked={eshopEdit.isEnabled}
              disabled={isPartnerDeactivated}
              onChange={(value) => setEshopEdit({ ...eshopEdit, isEnabled: value })}
            />
          </Form.Group>
          )
        }
        {
          eshopEdit.isBaseMarketplace && credentials && (
            <Form.Group>
              {credentials}
            </Form.Group>
          )
        }
        {
          inputs?.includes(Input.CLIENT_KEY) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.clientKey}
            </Form.ControlLabel>
            <Form.Control name="clientKey" type="text" autoComplete="off" />
            <Form.HelpText>Required</Form.HelpText>
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.CLIENT_SECRET) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.clientSecret}
            </Form.ControlLabel>
            <Form.Control name="clientSecret" type="text" autoComplete="off" />
            <Form.HelpText>Required</Form.HelpText>
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.STOCK_THRESHOLD) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.stockThreshold}
            </Form.ControlLabel>
            <Form.Control name="stockThreshold" accepter={InputNumber} />
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.EXPEDITION_DAYS) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.expeditionDays}
            </Form.ControlLabel>
            <Form.Control name="expeditionDays" accepter={InputNumber} />
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.FIXED_FEE) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.fixedFee}
            </Form.ControlLabel>
            <Form.Control name="fixedFee" accepter={InputNumber} />
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.DYNAMIC_BRANCH_ID) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.dynamicBranchId}
            </Form.ControlLabel>
            <Stack spacing={10}>
              <Toggle
                checked={eshopEdit.dynamicBranchId}
                onChange={(value) => setEshopEdit({ ...eshopEdit, dynamicBranchId: value })}
              />
              <RecalculateAlzaBranchIds dynamicBranchId={eshopSetup?.dynamicBranchId} />
            </Stack>
          </Form.Group>
          )
        }
        {
          inputs?.includes(Input.SUBSTITUTED_PHONE_NUMBER) && (
          <Form.Group>
            <Form.ControlLabel>
              {labels.substitutedPhoneNumber}
            </Form.ControlLabel>
            <Form.Control name="substitutedPhoneNumber" />
            <Form.HelpText>Optional</Form.HelpText>
          </Form.Group>
          )
        }
        {
            inputs?.includes(Input.PACKETA_ID) && (
            <Form.Group>
              <Form.ControlLabel>
                {labels.packetaId}
              </Form.ControlLabel>
              <Form.Control name="packetaId" />
              <Form.HelpText>Optional</Form.HelpText>
            </Form.Group>
            )
        }
        ,
        {
            inputs?.includes(Input.PACKETA_API_KEY) && (
            <Form.Group>
              <Form.ControlLabel>
                {labels.packetaApiKey}
              </Form.ControlLabel>
              <Form.Control name="packetaApiKey" />
              <Form.HelpText>Optional</Form.HelpText>
            </Form.Group>
            )
        }
        {
            inputs?.includes(Input.INVOICE_REQUIRED) && (
            <Form.Group>
              <Form.ControlLabel>
                {labels.invoiceRequired}
              </Form.ControlLabel>
              <Toggle
                checked={eshopEdit.invoiceRequired}
                onChange={(value) => setEshopEdit({ ...eshopEdit, invoiceRequired: value })}
              />
            </Form.Group>
            )
        }
        {
            inputs?.includes(Input.CREDIT_NOTE_REQUIRED) && (
            <Form.Group>
              <Form.ControlLabel>
                {labels.creditNoteRequired}
              </Form.ControlLabel>
              <Toggle
                checked={eshopEdit.creditNoteRequired}
                onChange={(value) => setEshopEdit({ ...eshopEdit, creditNoteRequired: value })}
              />
            </Form.Group>
            )
        }
        <Form.Group>
          <Form.ControlLabel>
            Print labels automatically
          </Form.ControlLabel>
          <Toggle
            checked={eshopEdit.automaticLabelsPrintEnabled}
            onChange={(value) => setEshopEdit({ ...eshopEdit, automaticLabelsPrintEnabled: value })}
          />
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>
            Select order status to automatically print labels.
          </Form.ControlLabel>
          <Form.Control
            name="automaticLabelsPrintOrderStatus"
            accepter={InputPicker}
            data={orderStates}
          />
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <Button appearance="primary" onClick={() => onSave()} type="submit">Save</Button>
            <Button appearance="ghost" onClick={() => onCancel()}>Cancel</Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
    </Stack>
  );
}

EshopSetupDetail.defaultProps = {
  credentials: undefined,
  inputs: [
    Input.SYNCHRONIZATION,
    Input.CLIENT_KEY,
    Input.CLIENT_SECRET,
    Input.STOCK_THRESHOLD,
    Input.EXPEDITION_DAYS,
    Input.SUBSTITUTED_PHONE_NUMBER,
  ],
  baseMarketplaceCodes: [],
  onIsBaseMarketplaceUpdate: undefined,
};
